import { Injectable, PLATFORM_ID, inject, signal, WritableSignal, computed, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '../models/language.model';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'esign-language';
  private readonly DEFAULT_LANGUAGE = 'en';

  private readonly MERGED_TRANSLATIONS_PATH = 'merged';

  public readonly currentLanguage: WritableSignal<string> = signal(this.DEFAULT_LANGUAGE);
  public readonly translationsLoaded: WritableSignal<boolean> = signal(false);

  private initializationPromise: Promise<void> | null = null;

  public readonly availableLanguages: Language[] = [
    { code: 'en', locale: 'en-US', name: 'English', flag: '🇺🇸' },
    { code: 'es', locale: 'es-US', name: 'Español', flag: '🇪🇸' }
  ];

  public readonly currentLanguageDetails: Signal<Language> = computed(() =>
    this.availableLanguages.find(l => l.code === this.currentLanguage()) ?? this.availableLanguages[0]
  );

  private readonly translate = inject(TranslateService);
  private readonly platformId: object = inject(PLATFORM_ID);

  public initializeApp(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.translationsLoaded()) {
      return Promise.resolve();
    }

    const selectedLang = this.getStoredLanguage();

    this.initializationPromise = new Promise((resolve) => {
      this.currentLanguage.set(selectedLang);

      const translationPath = `${this.MERGED_TRANSLATIONS_PATH}/${selectedLang}`;
      this.translate.setDefaultLang(translationPath);

      this.translate.use(translationPath).subscribe({
        next: () => {
          this.translationsLoaded.set(true);
          this.initializationPromise = null;
          resolve();
        },
        error: (error) => {
          console.warn(`Failed to load translations for ${selectedLang}, falling back to default`, error);

          this.currentLanguage.set(this.DEFAULT_LANGUAGE);

          const defaultPath = `${this.MERGED_TRANSLATIONS_PATH}/${this.DEFAULT_LANGUAGE}`;
          this.translate.setDefaultLang(defaultPath);

          this.translate.use(defaultPath).subscribe({
            next: () => {
              this.translationsLoaded.set(true);
              this.initializationPromise = null;
              resolve();
            },
            error: () => {
              console.error('Failed to load even default translations');
              this.translationsLoaded.set(true);
              this.initializationPromise = null;
              resolve();
            }
          });
        }
      });
    });

    return this.initializationPromise;
  }

  private getStoredLanguage(): string {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const storedLang = localStorage.getItem(this.STORAGE_KEY);
        if (storedLang && this.isValidLanguage(storedLang)) {
          return storedLang;
        }

        const browserLang = navigator.language.split('-')[0];
        if (browserLang && this.isValidLanguage(browserLang)) {
          return browserLang;
        }

        return this.DEFAULT_LANGUAGE;
      } catch {
        return this.DEFAULT_LANGUAGE;
      }
    }
    return this.DEFAULT_LANGUAGE;
  }

  private storeLanguage(language: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.STORAGE_KEY, language);
      } catch (error) {
        console.warn('Could not store language preference in localStorage:', error);
      }
    }
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage();
  }

  public setLanguage(language: string): void {
    if (this.isValidLanguage(language)) {
      this.currentLanguage.set(language);
      this.storeLanguage(language);

      const translationPath = `${this.MERGED_TRANSLATIONS_PATH}/${language}`;
      this.translate.setDefaultLang(translationPath);

      this.translate.use(translationPath).subscribe({
        next: () => {
          setTimeout(() => {
            this.translationsLoaded.set(true);
          }, 100);
        },
        error: () => {
          console.warn(`Failed to load translations for ${language}`);
          this.translationsLoaded.set(true);
        }
      });
    } else {
      console.warn(`Invalid language code: ${language}. Using default: ${this.DEFAULT_LANGUAGE}`);
      this.setLanguage(this.DEFAULT_LANGUAGE);
    }
  }

  private isValidLanguage(language: string): boolean {
    return this.availableLanguages.some(lang => lang.code === language);
  }

  public getLanguageDisplayName(code: string): string {
    const language = this.availableLanguages.find(lang => lang.code === code);
    return language ? language.name : code;
  }

  public getLanguageFlag(code: string): string {
    const language = this.availableLanguages.find(lang => lang.code === code);
    return language ? language.flag : '🌐';
  }

  public getNamespacedKey(module: string, key: string): string {
    return `${module}.${key}`;
  }
}